import { Component, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { AppNotificationService } from 'src/app/shared/services/app-notification.service';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UsersService } from '../../services/users-service';
import { User } from '../user-admin-page/user-model';
import { StorageKeys, StoreService } from '../../services/store.service';
import { AuthService } from '@auth0/auth0-angular';

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styles: [``],
    providers: [UsersService],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfileComponent implements OnInit {
  public user = signal<User | undefined>(undefined);
  public isEntraIdUser = signal<boolean>(false);
  
  // Claims from ID token for Entra-ID users
  public organizationFromClaim = signal<string | null>(null);
  public emailFromClaim = signal<string | null>(null);
  public firstNameFromClaim = signal<string | null>(null);
  public lastNameFromClaim = signal<string | null>(null);
  public jobTitleFromClaim = signal<string | null>(null);
  public highestRoleFromClaim = signal<string | null>(null);

  constructor(
    private _usersService: UsersService,
    private _storeService: StoreService,
    private _toaster: AppNotificationService,
    private _auth: AuthService
  ) {
  }

  async ngOnInit() : Promise<void> {
    const userData = await this._storeService.get<User>(StorageKeys.USER);
    this.user.set(userData);
    
    // Use isScimUser from User object (computed by backend) instead of checking token claims
    this.isEntraIdUser.set(userData?.isScimUser ?? false);
    
    try {
      // Only read ID token claims for Entra-ID users
      if (this.isEntraIdUser()) {
        const idToken = await firstValueFrom(
          this._auth.idTokenClaims$.pipe(filter(claims => claims !== null && claims !== undefined))
        );
        
        // Organization: try organization claim, current_db, or first company from companies array
        const organization = idToken?.['https://perifor.com/user/organization'] || 
                            idToken?.['https://perifor.com/user/current_db'] || null;
        this.organizationFromClaim.set(organization);
        
        // Fallback to first company in companies array if organization not set
        if (!this.organizationFromClaim()) {
          const companies = idToken?.['https://perifor.com/user/companies'];
          if (companies && Array.isArray(companies) && companies.length > 0) {
            this.organizationFromClaim.set(companies[0]);
          }
        }
        
        // Email: try standard email claim first, then namespaced
        const email = idToken?.['email'] || idToken?.['https://perifor.com/user/email'] || null;
        this.emailFromClaim.set(email);
        
        // Name: try to parse from standard name claim first, then namespaced
        const fullName = idToken?.['name'] || idToken?.['https://perifor.com/user/name'];
        if (fullName) {
          const nameParts = fullName.split(' ');
          this.firstNameFromClaim.set(nameParts[0] || null);
          this.lastNameFromClaim.set(nameParts.slice(1).join(' ') || null);
        } else {
          this.firstNameFromClaim.set(idToken?.['given_name'] || idToken?.['https://perifor.com/user/given_name'] || null);
          this.lastNameFromClaim.set(idToken?.['family_name'] || idToken?.['https://perifor.com/user/family_name'] || null);
        }
        
        this.jobTitleFromClaim.set(idToken?.['https://perifor.com/user/job_title'] || null);
        this.highestRoleFromClaim.set(idToken?.['https://perifor.com/user/highest_role'] || null);
      }
    } catch (error) {
      console.error('Error reading ID token claims:', error);
      // If claims reading fails, still use isScimUser from User object
    }
  }

  async onSetUserPassword(){
    const userData = this.user();
    if (!userData) {
      this._toaster.showError("User data not loaded.");
      return;
    }

    // Use profile email if valid, otherwise fall back to email from token
    const emailToReset = this.isValidEmail(userData.profile.email) 
      ? userData.profile.email 
      : this.emailFromClaim();
    
    if (!emailToReset) {
      this._toaster.showError("Unable to reset password: email address not found.");
      return;
    }
    
    const setPword = await lastValueFrom(this._usersService.resetPword(emailToReset));
    if (setPword) {
      this._toaster.showSuccess("Password reset email has been sent. Check your email.");
    }
  }

  private isValidEmail(email: string | undefined): boolean {
    if (!email) return false;
    // Check if email looks malformed (contains auth0| or @perifor.auto which are internal patterns)
    return !email.includes('auth0|') && !email.endsWith('@perifor.auto');
  }

}
